import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { CultureScore } from '@/lib/cultureEngine';
import {
  buildCultureExecutiveSummaryText,
  buildCultureOrganizationalImplications,
  buildCulturePracticalRecommendations,
  culturePdfExecutiveClosing,
  interpretCultureGaps,
} from '@/lib/culturePdfInterpretation';

export interface GenerateCulturePdfOptions {
  title: string;
  dateLabel: string;
  departmentLabel: string;
  actual: CultureScore;
  desired: CultureScore;
  alignmentPercent: number;
  /** Texto breve legado desde el dashboard; el PDF prioriza el resumen ejecutivo generado internamente. */
  interpretation: string;
  constructs: Array<{ label: string; description: string }>;
}

/** Márgenes horizontales y verticales (mm) — A4 con área de texto uniforme. */
const MARGIN = 18;
const LINE_HEIGHT_FACTOR = 1.38;

async function captureElementToBase64(element: HTMLElement): Promise<string> {
  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
  });
  return canvas.toDataURL('image/png');
}

function pageBottom(doc: jsPDF): number {
  return doc.internal.pageSize.getHeight() - MARGIN;
}

function textWidthMm(doc: jsPDF): number {
  return doc.internal.pageSize.getWidth() - MARGIN * 2;
}

/** Altura aproximada por línea (mm) coherente con fontSize y lineHeightFactor en jsPDF. */
function lineHeightMm(fontSize: number): number {
  return fontSize * LINE_HEIGHT_FACTOR * 0.3527;
}

/** Parte el título en "bloque fijo" + empresa para evitar cortes poco claros (p. ej. antes del nombre). */
function buildCulturePdfTitleLines(doc: jsPDF, rawTitle: string, maxW: number, fontSize: number): string[] {
  doc.setFontSize(fontSize);
  doc.setFont('helvetica', 'bold');
  const sep = ' - ';
  const i = rawTitle.indexOf(sep);
  if (i === -1) {
    return doc.splitTextToSize(rawTitle, maxW) as string[];
  }
  const left = rawTitle.slice(0, i).trim();
  const right = rawTitle.slice(i + sep.length).trim();
  const leftLines = doc.splitTextToSize(left, maxW) as string[];
  const rightLines = doc.splitTextToSize(right, maxW) as string[];
  return [...leftLines, ...rightLines];
}

/**
 * Párrafo justificado: usa líneas prepartidas a maxWidth y `align: 'justify'` de jsPDF.
 * Pagina automáticamente cuando el bloque no cabe.
 */
function placeJustifiedParagraph(
  doc: jsPDF,
  text: string,
  yStart: number,
  fontSize: number,
  afterSpacingMm: number
): number {
  const maxW = textWidthMm(doc);
  doc.setFontSize(fontSize);
  doc.setFont('helvetica', 'normal');
  const lines = doc.splitTextToSize(text.trim(), maxW);
  if (lines.length === 0) return yStart + afterSpacingMm;

  const lh = lineHeightMm(fontSize);
  let y = yStart;
  let idx = 0;

  while (idx < lines.length) {
    const bottom = pageBottom(doc);
    if (y + lh > bottom) {
      doc.addPage();
      y = MARGIN;
    }
    const maxLines = Math.max(1, Math.floor((pageBottom(doc) - y) / lh));

    const chunk = lines.slice(idx, idx + maxLines) as string[];
    doc.text(chunk, MARGIN, y, {
      maxWidth: maxW,
      align: 'justify',
      lineHeightFactor: LINE_HEIGHT_FACTOR,
    });
    y += chunk.length * lh;
    idx += chunk.length;

    if (idx < lines.length) {
      doc.addPage();
      y = MARGIN;
    }
  }

  return y + afterSpacingMm;
}

function placeSectionTitle(doc: jsPDF, title: string, y: number): number {
  const bottom = pageBottom(doc);
  if (y + 8 > bottom) {
    doc.addPage();
    y = MARGIN;
  }
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(title, MARGIN, y);
  return y + 7;
}

export async function generateCulturePdf(options: GenerateCulturePdfOptions): Promise<void> {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const maxW = textWidthMm(doc);
  let y = MARGIN;

  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  const titleLines = buildCulturePdfTitleLines(doc, options.title, maxW, 18);
  const pageCenterX = doc.internal.pageSize.getWidth() / 2;
  const lhTitle = lineHeightMm(18);
  let yTitle = y;
  for (const line of titleLines) {
    doc.text(line, pageCenterX, yTitle, { align: 'center', lineHeightFactor: LINE_HEIGHT_FACTOR });
    yTitle += lhTitle;
  }
  y = yTitle + 4;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(90, 90, 90);
  doc.text(`Fecha: ${options.dateLabel}`, MARGIN, y);
  y += 5;
  const deptLines = doc.splitTextToSize(`Departamento: ${options.departmentLabel}`, maxW);
  doc.text(deptLines, MARGIN, y, { maxWidth: maxW, align: 'left', lineHeightFactor: LINE_HEIGHT_FACTOR });
  y += deptLines.length * lineHeightMm(10) + 6;
  doc.setTextColor(0, 0, 0);

  if (y + 14 > pageBottom(doc)) {
    doc.addPage();
    y = MARGIN;
  }
  doc.setFillColor(248, 250, 252);
  doc.rect(MARGIN, y, maxW, 11, 'F');
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(`Alineación actual vs deseada: ${options.alignmentPercent.toFixed(1)}%`, MARGIN + 3, y + 7);
  y += 16;

  y = placeSectionTitle(doc, 'Resumen ejecutivo', y);
  doc.setFont('helvetica', 'normal');
  const executiveSummary = buildCultureExecutiveSummaryText(
    options.actual,
    options.desired,
    options.alignmentPercent
  );
  y = placeJustifiedParagraph(doc, executiveSummary, y, 9, 6);

  const radarEl = document.getElementById('culture-radar-chart');
  if (radarEl) {
    try {
      if (y + 90 > pageBottom(doc)) {
        doc.addPage();
        y = MARGIN;
      }
      const radarImg = await captureElementToBase64(radarEl);
      doc.addImage(radarImg, 'PNG', MARGIN, y, maxW, 78);
      y += 84;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(14, 165, 233);
      doc.text('Azul: Cultura Actual', MARGIN, y);
      doc.setTextColor(16, 185, 129);
      doc.text('Verde: Cultura Deseada', MARGIN + 55, y);
      doc.setTextColor(0, 0, 0);
      y += 8;
    } catch {
      // no-op
    }
  }

  if (y + 40 > pageBottom(doc)) {
    doc.addPage();
    y = MARGIN;
  }

  autoTable(doc, {
    startY: y,
    margin: { left: MARGIN, right: MARGIN },
    theme: 'grid',
    head: [['Dimensión', 'Cultura Actual', 'Cultura Deseada', 'Brecha (Deseada - Actual)']],
    body: [
      ['Clan', options.actual.clan.toFixed(2), options.desired.clan.toFixed(2), (options.desired.clan - options.actual.clan).toFixed(2)],
      ['Adocracia', options.actual.adhocracy.toFixed(2), options.desired.adhocracy.toFixed(2), (options.desired.adhocracy - options.actual.adhocracy).toFixed(2)],
      ['Mercado', options.actual.market.toFixed(2), options.desired.market.toFixed(2), (options.desired.market - options.actual.market).toFixed(2)],
      ['Jerarquía', options.actual.hierarchy.toFixed(2), options.desired.hierarchy.toFixed(2), (options.desired.hierarchy - options.actual.hierarchy).toFixed(2)],
    ],
    headStyles: { fillColor: [14, 165, 233], textColor: 255, fontStyle: 'bold' },
    styles: { fontSize: 9, cellPadding: 2, halign: 'center' },
    columnStyles: {
      0: { halign: 'left', cellWidth: 42 },
      1: { halign: 'center' },
      2: { halign: 'center' },
      3: { halign: 'center' },
    },
  });

  const finalY =
    (doc as jsPDF & { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY ?? y;
  y = finalY + 8;

  if (y + 24 > pageBottom(doc)) {
    doc.addPage();
    y = MARGIN;
  }
  y = placeSectionTitle(doc, 'Lectura de brechas culturales', y);
  const gapRows = interpretCultureGaps(options.actual, options.desired);
  for (const row of gapRows) {
    const block = `${row.dimension} (brecha ${row.gap > 0 ? '+' : ''}${row.gap.toFixed(2)}): ${row.text}`;
    y = placeJustifiedParagraph(doc, block, y, 9, 4);
  }

  if (y + 20 > pageBottom(doc)) {
    doc.addPage();
    y = MARGIN;
  }
  y = placeSectionTitle(doc, 'Implicaciones organizacionales', y);
  y = placeJustifiedParagraph(
    doc,
    buildCultureOrganizationalImplications(options.actual, options.desired),
    y,
    9,
    6
  );

  if (y + 20 > pageBottom(doc)) {
    doc.addPage();
    y = MARGIN;
  }
  y = placeSectionTitle(doc, 'Recomendaciones prácticas', y);
  y = placeJustifiedParagraph(
    doc,
    buildCulturePracticalRecommendations(options.actual, options.desired),
    y,
    9,
    6
  );

  if (y + 20 > pageBottom(doc)) {
    doc.addPage();
    y = MARGIN;
  }
  y = placeSectionTitle(doc, 'Cierre ejecutivo', y);
  y = placeJustifiedParagraph(doc, culturePdfExecutiveClosing(), y, 9, 6);

  if (y + 15 > pageBottom(doc)) {
    doc.addPage();
    y = MARGIN;
  }

  y = placeSectionTitle(doc, 'Significado de las dimensiones', y);

  for (const construct of options.constructs) {
    if (y + 10 > pageBottom(doc)) {
      doc.addPage();
      y = MARGIN;
    }
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(`${construct.label}:`, MARGIN, y);
    y += 4;
    doc.setFont('helvetica', 'normal');
    y = placeJustifiedParagraph(doc, construct.description, y, 9, 4);
  }

  const dateStr = new Date().toISOString().slice(0, 10);
  doc.save(`Cultura_Organizacional_${dateStr}.pdf`);
}
